import React, { useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LoginForm from "./components/auth/LoginForm";
import SignUpForm from "./components/auth/SignUpForm";
import NavBar from "./components/NavBar";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Search from "./components/Search";
import SearchArea from "./components/Search/SearchArea";
import Appointments from "./components/Appointments";
import Notification from "./components/Tools/Notification";
import Agents from "./components/Agents";
import Agent from "./components/Agent";
import NotFound from "./components/NotFound";
import Profile from "./components/Profile";
import Reviews from "./components/Reviews";
import Chats from "./components/Chats";
import { Link } from "react-router-dom";
import { authenticate } from "./store/session";
import { initWhitelabel } from "./store/whitelabel";

import About from "./components/About";
import Home from "./components/Home";
import AgentLanding from "./components/AgentLanding";
import PPTVideo from "./components/PPTVideo";
import NewHomeVideo from "./components/NewHomeVideo";
import Career from "./components/Career";
import AgentLogin from "./components/AgentLogin";
import ListingPage from "./components/Property/ListingPage";
import VideoFromExtension from "./components/VideoFromExtension";
import UnreadNotifier from "./components/NavBar/UnreadNotifier";
import { ChatBubbleProvider } from "./context/ChatBubble";
import ChatBubble from "./components/ChatBubble";

const DEFAULT_AREA = "/area/neLat=43.885&neLng=-79.285&swLat=43.850&swLng=-79.335&zoom=14"; // Unionville

// Keeps a hidden DOM element in sync with agent login status so the
// Tourit Chrome extension content script can read it.
const AgentStatusEmbed = () => {
	const user = useSelector((state) => state.session.user);
	useEffect(() => {
		let el = document.getElementById('tourit-user-data');
		if (!el) {
			el = document.createElement('script');
			el.id = 'tourit-user-data';
			el.type = 'application/json';
			document.head.appendChild(el);
		}
		const agentSlug = user?.agent && user.username
			? user.username.toLowerCase().replace(/[^a-z0-9]/g, '')
			: null;
		el.textContent = JSON.stringify({
			is_agent:    !!(user?.agent),
			account_key: user?.agent ? `agent_${user.id}` : null,
			agent_slug:  agentSlug,
		});
		return () => { try { el.remove(); } catch {} };
	}, [user]);
	return null;
};

// Runs once after Google OAuth callback: if sessionStorage has a saved listing,
// navigate to it so the user can resume booking.
const TourReturnHandler = () => {
	const history = useHistory();
	const user = useSelector((state) => state.session.user);

	useEffect(() => {
		if (!user) return;
		const raw = sessionStorage.getItem("tourReturn");
		if (!raw) return;
		try {
			const { path, propertyId } = JSON.parse(raw);
			if (path) {
				history.replace(path);
				return;
			}
			history.replace(`${DEFAULT_AREA}?selected=${encodeURIComponent(propertyId)}`);
		} catch {
			sessionStorage.removeItem("tourReturn");
		}
	}, [user]); // eslint-disable-line react-hooks/exhaustive-deps

	return null;
};

// Redirects a logged-in agent from the main tourit.ca domain to their own subdomain.
const AgentWhitelabelRedirect = () => {
	const user        = useSelector(s => s.session.user);
	const authChecked = useSelector(s => s.session.authChecked);
	useEffect(() => {
		if (!authChecked || !user?.agent) return;
		const hostname = window.location.hostname;
		if (hostname === 'localhost' || hostname === '127.0.0.1') return;
		// Already on a *.tourit.ca subdomain — stay put.
		if (/^[a-z0-9-]+\.tourit\.ca$/i.test(hostname) && hostname.toLowerCase() !== 'www.tourit.ca') return;
		const slug = user.username.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
		if (!slug) return;
		window.location.href = `https://${slug}.tourit.ca`;
	}, [user, authChecked]);
	return null;
};

// Updates the browser tab title for whitelabel agent sites
const WhitelabelTitle = () => {
	const agent = useSelector((state) => state.whitelabel?.agent);
	useEffect(() => {
		document.title = "加家团队";
	}, [agent]);
	return null;
};

function App() {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(authenticate());
		dispatch(initWhitelabel());
		fetch(`${process.env.REACT_APP_API_URL || "https://api.tourit.ca"}/warmup`).catch(() => {});
	}, [dispatch]);

	return (
		<ChatBubbleProvider>
		<BrowserRouter>
			<AgentStatusEmbed />
			<AgentWhitelabelRedirect />
			<WhitelabelTitle />
			<TourReturnHandler />
			<UnreadNotifier />
			<NavBar />
			<Notification />
<Switch>
				<Route path="/" exact={true}>
					<Home />
				</Route>
				<Route path="/search/:searchParam" exact={true}>
					<Search />
				</Route>
				<Route path="/area/:areaParam" exact={true}>
					<SearchArea />
				</Route>
				<Route path="/login" exact={true}>
					<LoginForm />
				</Route>
				<Route path="/sign-up" exact={true}>
					<SignUpForm />
				</Route>
				<Route path="/agents" exact={true}>
					<Agents />
				</Route>
				<Route path="/agents/:agentId">
					<Agent />
				</Route>
				<Route path="/about" exact={true}>
					<About />
				</Route>
				<Route path="/for-agents" exact={true}>
					<AgentLanding />
				</Route>
				<Route path="/career" exact={true}>
					<Career />
				</Route>
				<Route path="/agent-login" exact={true}>
					<AgentLogin />
				</Route>
				<ProtectedRoute path="/make-video" exact={true}>
					<VideoFromExtension />
				</ProtectedRoute>
				<Route path="/a/:agentId/listing/:mlsNumber" exact={true}>
					<ListingPage />
				</Route>
				<Route path="/listing/:mlsNumber" exact={true}>
					<ListingPage />
				</Route>
			<ProtectedRoute path="/ppt-video" exact={true}>
					<PPTVideo />
				</ProtectedRoute>
			<ProtectedRoute path="/new-home-video" exact={true}>
					<NewHomeVideo />
				</ProtectedRoute>
<ProtectedRoute path="/appointments" exact={true}>
					<Appointments />
				</ProtectedRoute>
				<ProtectedRoute path="/profile" exact={true}>
					<Profile />
				</ProtectedRoute>
				<ProtectedRoute path="/reviews" exact={true}>
					<Reviews />
				</ProtectedRoute>
				<ProtectedRoute path={["/chats", "/chats/:channelId"]} exact={true}>
					<Chats />
				</ProtectedRoute>
				<Route>
					<NotFound />
				</Route>
			</Switch>
			<footer className="app-footer">
				<span translate="no">&copy; 2026 加家团队 · Canada Home Team &nbsp;&middot;&nbsp; Bay Street Group Inc. Brokerage</span>
				<Link to="/agent-login" className="app-footer-agent-link">Agent Login</Link>
			</footer>
			<ChatBubble />
		</BrowserRouter>
		</ChatBubbleProvider>
	);
}

export default App;
